// Inventory Page — regional demand dashboard
import { useState, useEffect } from "react";
import { api, getApiErrorMessage } from "@/services/api";
import { PageHeader, Card, LoadingSpinner, Alert } from "@/components/shared";
import { Province, ProvinceLabels, PropertyTypeLabels, type InventorySummary } from "@/types";
import { MapPin } from "lucide-react";

export function InventoryPage() {
  const [summaries, setSummaries] = useState<InventorySummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const result = await api.get<{ data: InventorySummary[] }>("/inventory/summary");
        setSummaries(result.data);
        setError(null);
      } catch (err) {
        setError(getApiErrorMessage(err));
      }
      finally { setIsLoading(false); }
    }
    load();
  }, []);

  if (isLoading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;

  return (
    <div>
      <PageHeader
        title="Regional Inventory"
        description="Market demand overview by province and suburb — no owner-identifiable information displayed."
      />

      {error && <Alert type="error" className="mb-6">{error}</Alert>}

      <div className="grid md:grid-cols-3 gap-6">
        {Object.values(Province).map((province) => {
          const summary = summaries.find((s) => s.province === province);
          return (
            <Card key={province}>
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-5 w-5 text-sun" />
                <h3 className="font-semibold text-white">{ProvinceLabels[province]}</h3>
              </div>
              <div className="stat-value mb-2">
                {summary?.total_active || 0}
              </div>
              <p className="text-muted mb-4">active requests</p>

              {summary?.suburbs && summary.suburbs.length > 0 ? (
                <div className="space-y-2">
                  {summary.suburbs.slice(0, 5).map((s) => (
                    <div key={s.suburb} className="flex items-center justify-between text-sm">
                      <span className="text-white/70">{s.suburb}</span>
                      <span className="text-white/50">{s.active_requests} requests</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted italic">No active requests</p>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
